// Libs
import { useState, useRef, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import TagIcon from "@heroicons/react/24/outline/TagIcon";
import ExclamationTriangleIcon from "@heroicons/react/24/outline/ExclamationTriangleIcon";
import ArrowUturnLeftIcon from "@heroicons/react/24/outline/ArrowUturnLeftIcon";
import ArrowUturnRightIcon from "@heroicons/react/24/outline/ArrowUturnRightIcon";
import SparklesIcon from "@heroicons/react/24/outline/SparklesIcon";
import ChatBubbleLeftRightIcon from "@heroicons/react/24/outline/ChatBubbleLeftRightIcon";
import RocketLaunchIcon from "@heroicons/react/24/outline/RocketLaunchIcon";
import PaperAirplaneIcon from "@heroicons/react/24/outline/PaperAirplaneIcon";

// Local
import BaseInputText from "../BaseInputText";
import BaseInputTextArea from "../BaseInputTextArea";
import BaseInputSelect from "../BaseInputSelect";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import StiltonEmailAssistant from "./StiltonEmailAssistant";
import SendTestEmailDialog from "./SendTestEmailDialog";
import CopyTemplateDialog from "./CopyTemplateDialog";
import ConfirmationDialog from "../ConfirmationDialog";
import ImageSelector from "./ImageSelector";
import {
  EmailTemplateStatus,
  TemplateVariable,
  ExistingEmailTemplate,
} from "../../models/email-template";
import { EMAIL_CAMPAIGNS_CREATE_URL } from "../../urls";

interface FormData {
  name: string;
  subject: string;
  description?: string;
  htmlContent: string;
  status: EmailTemplateStatus;
}

interface Props {
  initialData?: Partial<FormData>;
  onSubmit: (data: FormData) => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  isLoading?: boolean;
  templateId?: string;
  // Template copying
  templates?: ExistingEmailTemplate[];
  templatesLoading?: boolean;
  onTemplateCopied?: () => void;
  // AI Assistant props
  siteKey?: string;
  onGenerateWithAI?: (data: {
    description: string;
    conversationID?: string;
  }) => Promise<{
    htmlContent: string;
    subject: string;
    conversationID: string;
  }>;
  onEditWithAI?: (data: {
    editInstructions: string;
    currentHtmlContent: string;
    currentSubject: string;
    conversationID?: string;
  }) => Promise<{
    htmlContent: string;
    subject: string;
    conversationID: string;
  }>;
  conversationID?: string;
}

interface UndoRedoState {
  content: string;
  cursorPosition: number;
}

export default function EmailTemplateForm({
  initialData,
  onSubmit,
  onCancel,
  isEditing = false,
  isLoading = false,
  templateId,
  templates = [],
  templatesLoading = false,
  onTemplateCopied,
  siteKey,
  onGenerateWithAI,
  onEditWithAI,
  conversationID,
}: Props) {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const htmlContentRef = useRef<HTMLTextAreaElement>(null);
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const [isInlineEditMode, setIsInlineEditMode] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"stilton" | "html">("stilton");

  // Test email dialog state
  const [isTestEmailDialogOpen, setIsTestEmailDialogOpen] = useState(false);

  // Copy template dialog state
  const [isCopyTemplateDialogOpen, setIsCopyTemplateDialogOpen] =
    useState(false);

  // Confirmation dialog state for unsaved changes
  const [isConfirmCancelDialogOpen, setIsConfirmCancelDialogOpen] =
    useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Undo/Redo state management
  const [undoStack, setUndoStack] = useState<UndoRedoState[]>([]);
  const [redoStack, setRedoStack] = useState<UndoRedoState[]>([]);
  const [lastSavedState, setLastSavedState] = useState<string>("");

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: initialData,
  });

  // Create a callback ref that handles both refs
  const { ref: registerRef, ...htmlContentRegister } = register("htmlContent", {
    required: "Email content is required",
  });

  const handleTextareaRef = useCallback(
    (element: HTMLTextAreaElement | null) => {
      registerRef(element);
      if (htmlContentRef.current !== element) {
        (
          htmlContentRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = element;
      }
    },
    [registerRef],
  );

  const watchedHtmlContent = watch("htmlContent");
  const watchedSubject = watch("subject");

  // Initialize undo/redo state
  useEffect(() => {
    const initialContent = initialData?.htmlContent || "";
    setLastSavedState(initialContent);
    if (initialContent) {
      setUndoStack([{ content: initialContent, cursorPosition: 0 }]);
    }
  }, [initialData?.htmlContent]);

  // Save state for undo/redo when content changes
  const saveStateForUndo = useCallback(
    (content: string, cursorPosition: number) => {
      if (content !== lastSavedState) {
        setUndoStack((prev) => [
          ...prev,
          { content: lastSavedState, cursorPosition },
        ]);
        setRedoStack([]); // Clear redo stack when new action is performed
        setLastSavedState(content);

        // Limit undo stack size
        setUndoStack((prev) => (prev.length > 50 ? prev.slice(1) : prev));
      }
    },
    [lastSavedState],
  );

  // Handle undo
  const handleUndo = useCallback(() => {
    if (undoStack.length > 0) {
      const currentState = {
        content: watchedHtmlContent,
        cursorPosition: htmlContentRef.current?.selectionStart || 0,
      };
      const previousState = undoStack[undoStack.length - 1];

      setRedoStack((prev) => [...prev, currentState]);
      setUndoStack((prev) => prev.slice(0, -1));

      setValue("htmlContent", previousState.content);
      setLastSavedState(previousState.content);

      // Restore cursor position
      setTimeout(() => {
        if (htmlContentRef.current) {
          htmlContentRef.current.focus();
          htmlContentRef.current.setSelectionRange(
            previousState.cursorPosition,
            previousState.cursorPosition,
          );
        }
      }, 0);
    }
  }, [undoStack, watchedHtmlContent, setValue]);

  // Handle redo
  const handleRedo = useCallback(() => {
    if (redoStack.length > 0) {
      const currentState = {
        content: watchedHtmlContent,
        cursorPosition: htmlContentRef.current?.selectionStart || 0,
      };
      const nextState = redoStack[redoStack.length - 1];

      setUndoStack((prev) => [...prev, currentState]);
      setRedoStack((prev) => prev.slice(0, -1));

      setValue("htmlContent", nextState.content);
      setLastSavedState(nextState.content);

      // Restore cursor position
      setTimeout(() => {
        if (htmlContentRef.current) {
          htmlContentRef.current.focus();
          htmlContentRef.current.setSelectionRange(
            nextState.cursorPosition,
            nextState.cursorPosition,
          );
        }
      }, 0);
    }
  }, [redoStack, watchedHtmlContent, setValue]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (
        (e.metaKey || e.ctrlKey) &&
        (e.key === "y" || (e.key === "z" && e.shiftKey))
      ) {
        e.preventDefault();
        handleRedo();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Template variables for easy insertion
  const templateVariables = [
    { label: "Customer Name", value: TemplateVariable.CUSTOMER_NAME },
    { label: "Date", value: TemplateVariable.DATE },
  ];

  const statusOptions = [
    { label: "Draft", value: EmailTemplateStatus.DRAFT },
    { label: "Active", value: EmailTemplateStatus.ACTIVE },
    { label: "Archived", value: EmailTemplateStatus.ARCHIVED },
  ];

  const insertVariable = useCallback(
    (variable: TemplateVariable) => {
      if (htmlContentRef.current) {
        const textarea = htmlContentRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = textarea.value;

        // Save state for undo before making changes
        saveStateForUndo(currentValue, start);

        const newValue =
          currentValue.substring(0, start) +
          variable +
          currentValue.substring(end);

        setValue("htmlContent", newValue);

        // Set cursor position after inserted variable with better timing
        requestAnimationFrame(() => {
          textarea.focus();
          const newPosition = start + variable.length;
          textarea.setSelectionRange(newPosition, newPosition);
        });
      }
    },
    [setValue, saveStateForUndo],
  );

  const insertImage = useCallback(
    (imageUrl: string) => {
      if (htmlContentRef.current) {
        const textarea = htmlContentRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = textarea.value;

        // Save state for undo before making changes
        saveStateForUndo(currentValue, start);

        // Create responsive image HTML with modern styling
        const imageHtml = `<img src="${imageUrl}" alt="Email image" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;

        const newValue =
          currentValue.substring(0, start) +
          imageHtml +
          currentValue.substring(end);

        setValue("htmlContent", newValue);

        // Set cursor position after inserted image with better timing
        requestAnimationFrame(() => {
          textarea.focus();
          const newPosition = start + imageHtml.length;
          textarea.setSelectionRange(newPosition, newPosition);
        });
      }
    },
    [setValue, saveStateForUndo],
  );

  // Handle content changes for undo/redo
  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newContent = e.target.value;
      const cursorPosition = e.target.selectionStart;

      // Save state for undo after a delay to avoid saving every keystroke
      setTimeout(() => {
        saveStateForUndo(newContent, cursorPosition);
      }, 500);
    },
    [saveStateForUndo],
  );

  // AI Assistant handlers
  const handleApplyAIChanges = useCallback(
    (htmlContent: string, subject: string) => {
      // Save current state for undo
      saveStateForUndo(
        watchedHtmlContent,
        htmlContentRef.current?.selectionStart || 0,
      );

      // Apply the AI-generated content
      setValue("htmlContent", htmlContent);
      setValue("subject", subject);

      // Update last saved state
      setLastSavedState(htmlContent);
    },
    [watchedHtmlContent, saveStateForUndo, setValue],
  );

  const validateTemplate = useCallback(() => {
    const errors: string[] = [];
    const mustachePattern = /\{\{[^}]+\}\}/g;
    const matches = watchedHtmlContent.match(mustachePattern) || [];
    const validVariables = Object.values(TemplateVariable);

    matches.forEach((match) => {
      if (!validVariables.includes(match as TemplateVariable)) {
        errors.push(`Invalid template variable: ${match}`);
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  }, [watchedHtmlContent]);

  const onFormSubmit = async (data: FormData) => {
    if (!validateTemplate()) {
      return;
    }

    // Clean up any editing attributes from the HTML content before submitting
    const cleanupDiv = document.createElement("div");
    cleanupDiv.innerHTML = data.htmlContent;
    const allElements = cleanupDiv.querySelectorAll("*");
    allElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.removeAttribute("contenteditable");
      htmlElement.removeAttribute("data-edit-id");
      htmlElement.removeAttribute("data-original-text");
      htmlElement.removeAttribute("data-original-html");
      htmlElement.removeAttribute("title");
      if (htmlElement.style.outline) htmlElement.style.outline = "";
      if (htmlElement.style.outlineOffset) htmlElement.style.outlineOffset = "";
      if (htmlElement.style.cursor === "text") htmlElement.style.cursor = "";
      if (htmlElement.style.length === 0) {
        htmlElement.removeAttribute("style");
      }
    });

    // Submit with cleaned HTML content
    const cleanedData = {
      ...data,
      htmlContent: cleanupDiv.innerHTML,
    };

    await onSubmit(cleanedData);
  };

  const generatePreviewContent = () => {
    let previewHtml = watchedHtmlContent;
    let previewSubject = watchedSubject;

    // If previewHtml is undefined, set it to an empty string
    if (!previewHtml) {
      previewHtml = "";
    }

    if (!previewSubject) {
      previewSubject = "";
    }

    // Replace variables with sample data
    const sampleData = {
      [TemplateVariable.CUSTOMER_NAME]: "John Doe",
      [TemplateVariable.DATE]: "April 27, 2024",
    };

    Object.entries(sampleData).forEach(([variable, value]) => {
      previewHtml = previewHtml.replace(new RegExp(variable, "g"), value);
      previewSubject = previewSubject.replace(new RegExp(variable, "g"), value);
    });

    return { previewHtml, previewSubject };
  };

  const { previewHtml, previewSubject } = generatePreviewContent();

  // Inline editing functions
  const enableInlineEditing = useCallback(() => {
    if (!previewRef.current) return;

    const editableElements = previewRef.current.querySelectorAll(
      "p, h1, h2, h3, h4, h5, h6, div, span, td, th, li",
    );

    editableElements.forEach((element, index) => {
      const htmlElement = element as HTMLElement;
      // Only make elements with text content editable
      if (htmlElement.textContent?.trim()) {
        htmlElement.contentEditable = "true";
        htmlElement.style.outline = "1px dashed #3b82f6";
        htmlElement.style.outlineOffset = "2px";
        htmlElement.style.cursor = "text";
        htmlElement.title = "Click to edit this text";

        // Add unique identifier for reliable matching
        htmlElement.setAttribute("data-edit-id", `editable-${index}`);
        htmlElement.setAttribute(
          "data-original-text",
          htmlElement.textContent || "",
        );
        htmlElement.setAttribute(
          "data-original-html",
          htmlElement.innerHTML || "",
        );

        // Add event listeners for editing
        htmlElement.addEventListener("blur", handleInlineEditBlur);
      }
    });
  }, []);

  const disableInlineEditing = useCallback(() => {
    if (!previewRef.current) return;

    const editableElements = previewRef.current.querySelectorAll(
      '[contenteditable="true"]',
    );

    editableElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.contentEditable = "false";
      htmlElement.style.outline = "";
      htmlElement.style.outlineOffset = "";
      htmlElement.style.cursor = "";
      htmlElement.title = "";

      // Remove data attributes
      htmlElement.removeAttribute("data-edit-id");
      htmlElement.removeAttribute("data-original-text");
      htmlElement.removeAttribute("data-original-html");

      // Remove event listeners
      htmlElement.removeEventListener("blur", handleInlineEditBlur);
    });

    // Also clean up the form's HTML content to ensure no editing attributes persist
    const currentHtml = watchedHtmlContent;
    const cleanupDiv = document.createElement("div");
    cleanupDiv.innerHTML = currentHtml;
    const allElements = cleanupDiv.querySelectorAll("*");
    allElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.removeAttribute("contenteditable");
      htmlElement.removeAttribute("data-edit-id");
      htmlElement.removeAttribute("data-original-text");
      htmlElement.removeAttribute("data-original-html");
      htmlElement.removeAttribute("title");
      if (htmlElement.style.outline) htmlElement.style.outline = "";
      if (htmlElement.style.outlineOffset) htmlElement.style.outlineOffset = "";
      if (htmlElement.style.cursor === "text") htmlElement.style.cursor = "";
      if (htmlElement.style.length === 0) {
        htmlElement.removeAttribute("style");
      }
    });

    const cleanedHtml = cleanupDiv.innerHTML;
    if (cleanedHtml !== currentHtml) {
      setValue("htmlContent", cleanedHtml, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [watchedHtmlContent, setValue]);

  const handleInlineEditBlur = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    const currentText = target.textContent || "";
    const originalText = target.getAttribute("data-original-text") || "";

    // Check if content has changed (either text content or HTML structure)
    if (
      currentText !== originalText ||
      target.innerHTML !== target.getAttribute("data-original-html")
    ) {
      // Update the HTML content in the form
      updateHtmlFromInlineEdit(target);

      // Update the stored original values
      target.setAttribute("data-original-text", currentText);
      target.setAttribute("data-original-html", target.innerHTML);
    }
  }, []);

  const updateHtmlFromInlineEdit = useCallback(
    (editedElement: HTMLElement) => {
      // Save current state for undo
      saveStateForUndo(watchedHtmlContent, 0);

      // Get the edit ID and new content
      const editId = editedElement.getAttribute("data-edit-id");
      const newHtmlContent = editedElement.innerHTML;

      if (!editId) {
        console.warn("No edit ID found for element");
        return;
      }

      // Create a temporary div to parse the current HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = watchedHtmlContent;

      // Add edit IDs to the original HTML elements for matching
      const editableElements = tempDiv.querySelectorAll(
        "p, h1, h2, h3, h4, h5, h6, div, span, td, th, li",
      );
      editableElements.forEach((element, index) => {
        const htmlElement = element as HTMLElement;
        if (htmlElement.textContent?.trim()) {
          htmlElement.setAttribute("data-edit-id", `editable-${index}`);
        }
      });

      // Find the matching element by edit ID
      const targetElement = tempDiv.querySelector(`[data-edit-id="${editId}"]`);

      if (targetElement) {
        // Update the content
        targetElement.innerHTML = newHtmlContent;

        // Remove the edit ID from the updated HTML
        targetElement.removeAttribute("data-edit-id");
      } else {
        console.warn(`Could not find element with edit ID: ${editId}`);
      }

      // Update the form with the new HTML
      const updatedHtml = tempDiv.innerHTML;

      // Clean up editing attributes inline
      const cleanupDiv = document.createElement("div");
      cleanupDiv.innerHTML = updatedHtml;
      const allElements = cleanupDiv.querySelectorAll("*");
      allElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.removeAttribute("contenteditable");
        htmlElement.removeAttribute("data-edit-id");
        htmlElement.removeAttribute("data-original-text");
        htmlElement.removeAttribute("data-original-html");
        htmlElement.removeAttribute("title");
        if (htmlElement.style.outline) htmlElement.style.outline = "";
        if (htmlElement.style.outlineOffset)
          htmlElement.style.outlineOffset = "";
        if (htmlElement.style.cursor === "text") htmlElement.style.cursor = "";
        if (htmlElement.style.length === 0) {
          htmlElement.removeAttribute("style");
        }
      });
      const cleanedHtml = cleanupDiv.innerHTML;

      setValue("htmlContent", cleanedHtml, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });

      console.log("Updated HTML content:", cleanedHtml);
    },
    [watchedHtmlContent, saveStateForUndo, setValue],
  );

  // Toggle inline edit mode
  const toggleInlineEditMode = useCallback(() => {
    const newMode = !isInlineEditMode;
    setIsInlineEditMode(newMode);

    if (newMode) {
      // Enable inline editing with a small delay to ensure DOM is ready
      setTimeout(() => {
        enableInlineEditing();
      }, 100);
    } else {
      disableInlineEditing();
    }
  }, [isInlineEditMode, enableInlineEditing, disableInlineEditing]);

  // Cleanup effect to ensure no editing attributes are left when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any editing attributes when component unmounts
      const currentHtml = watchedHtmlContent;
      if (
        currentHtml &&
        (currentHtml.includes("contenteditable") ||
          currentHtml.includes("data-edit-id"))
      ) {
        const cleanupDiv = document.createElement("div");
        cleanupDiv.innerHTML = currentHtml;
        const allElements = cleanupDiv.querySelectorAll("*");
        allElements.forEach((element) => {
          const htmlElement = element as HTMLElement;
          htmlElement.removeAttribute("contenteditable");
          htmlElement.removeAttribute("data-edit-id");
          htmlElement.removeAttribute("data-original-text");
          htmlElement.removeAttribute("data-original-html");
          htmlElement.removeAttribute("title");
          if (htmlElement.style.outline) htmlElement.style.outline = "";
          if (htmlElement.style.outlineOffset)
            htmlElement.style.outlineOffset = "";
          if (htmlElement.style.cursor === "text")
            htmlElement.style.cursor = "";
          if (htmlElement.style.length === 0) {
            htmlElement.removeAttribute("style");
          }
        });

        const cleanedHtml = cleanupDiv.innerHTML;
        setValue("htmlContent", cleanedHtml, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    };
  }, [watchedHtmlContent, setValue]);

  const handleCreateCampaign = () => {
    if (templateId) {
      navigate(`${EMAIL_CAMPAIGNS_CREATE_URL}?templateId=${templateId}`);
    }
  };

  // Test email handlers
  const handleSendTestEmail = () => {
    setIsTestEmailDialogOpen(true);
  };

  const handleCloseTestEmailDialog = () => {
    setIsTestEmailDialogOpen(false);
  };

  const handleCopyFromTemplate = () => {
    setIsCopyTemplateDialogOpen(true);
  };

  const handleCloseCopyTemplateDialog = () => {
    setIsCopyTemplateDialogOpen(false);
  };

  const handleCopyTemplate = (template: ExistingEmailTemplate) => {
    // Copy template content to form
    setValue("name", `Copy of ${template.name}`);
    setValue("subject", template.subject);
    setValue("description", template.description || "");
    setValue("htmlContent", template.htmlContent);

    // Save state for undo
    const cursorPosition = htmlContentRef.current?.selectionStart || 0;
    saveStateForUndo(template.htmlContent, cursorPosition);

    // Notify parent component that template was copied
    if (onTemplateCopied) {
      onTemplateCopied();
    }

    setIsCopyTemplateDialogOpen(false);
  };

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    const currentValues = getValues();

    // Compare current form values with initial data
    const initialName = initialData?.name || "";
    const initialSubject = initialData?.subject || "";
    const initialDescription = initialData?.description || "";
    const initialHtmlContent = initialData?.htmlContent || "";
    const initialStatus = initialData?.status || EmailTemplateStatus.DRAFT;

    return (
      currentValues.name !== initialName ||
      currentValues.subject !== initialSubject ||
      (currentValues.description || "") !== initialDescription ||
      currentValues.htmlContent !== initialHtmlContent ||
      currentValues.status !== initialStatus
    );
  }, [getValues, initialData]);

  // Handle cancel with confirmation if there are unsaved changes
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges() && !isCancelling) {
      setIsConfirmCancelDialogOpen(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, isCancelling, onCancel]);

  // Handle confirmed cancel action
  const handleConfirmCancel = useCallback(async () => {
    setIsCancelling(true);
    setIsConfirmCancelDialogOpen(false);
    onCancel();
  }, [onCancel]);

  // Handle cancel dialog close
  const handleCancelDialogClose = useCallback(() => {
    setIsConfirmCancelDialogOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div className="w-full">
          <div className="mb-4 text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? "Edit Email Template" : "Create Email Template"}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {isEditing
                ? "Update your email template details and content"
                : "Create a new email template for your marketing campaigns"}
            </p>
          </div>

          {/* Form Actions - Full Width */}
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex justify-between">
              <div className="flex space-x-3">
                {/* Copy from Template Button - Only show when creating new template */}
                {!isEditing && templates.length > 0 && (
                  <BaseButtonSecondary
                    type="button"
                    onClick={handleCopyFromTemplate}
                    className="flex items-center gap-2"
                    disabled={isLoading || templatesLoading}
                  >
                    <TagIcon className="h-4 w-4" />
                    Copy from Existing Template
                  </BaseButtonSecondary>
                )}

                {/* Create Campaign Button - Only show when editing existing template */}
                {isEditing && templateId && (
                  <BaseButtonSecondary
                    type="button"
                    onClick={handleCreateCampaign}
                    className="flex items-center gap-2"
                  >
                    <RocketLaunchIcon className="h-4 w-4" />
                    Create Campaign With This Template
                  </BaseButtonSecondary>
                )}

                {/* Send Test Email Button - Only show when editing and have required data */}
                {isEditing && templateId && siteKey && (
                  <BaseButtonSecondary
                    type="button"
                    onClick={handleSendTestEmail}
                    className="flex items-center gap-2"
                    disabled={isLoading}
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                    Send Test Email
                  </BaseButtonSecondary>
                )}
              </div>

              <div className="flex space-x-4">
                <BaseButtonSecondary
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </BaseButtonSecondary>
                <BaseButtonPrimary
                  type="submit"
                  disabled={isLoading || validationErrors.length > 0}
                  isBusy={isLoading}
                  busyText="Saving..."
                >
                  {isLoading
                    ? "Saving..."
                    : isEditing
                      ? "Save"
                      : "Create Template"}
                </BaseButtonPrimary>
              </div>
            </div>
          </div>

          {/* Template Details - Full Width */}
          <div className="my-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-medium text-gray-900">
              Template Details
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Template Name */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Template Name
                </label>
                <BaseInputText
                  admin={true}
                  {...register("name", {
                    required: "Template name is required",
                  })}
                  placeholder="Enter template name"
                />
              </div>

              {/* Description */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Description
                </label>
                <BaseInputTextArea
                  admin={true}
                  {...register("description")}
                  placeholder="Describe what this template is for"
                  rows={2}
                />
              </div>
              {/* Status */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Status
                </label>
                <BaseInputSelect admin={true} {...register("status")}>
                  <option value={EmailTemplateStatus.DRAFT}>Draft</option>
                  <option value={EmailTemplateStatus.ACTIVE}>Active</option>
                  <option value={EmailTemplateStatus.ARCHIVED}>Archived</option>
                </BaseInputSelect>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Left Column - Email Content */}
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
              {/* Tab Header */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex">
                  <button
                    type="button"
                    onClick={() => setActiveTab("stilton")}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                      activeTab === "stilton"
                        ? "border-b-2 border-purple-500 bg-purple-50 text-purple-600"
                        : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <SparklesIcon className="h-4 w-4" />
                      Edit With Stilton
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("html")}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                      activeTab === "html"
                        ? "border-b-2 border-blue-500 bg-blue-50 text-blue-600"
                        : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <TagIcon className="h-4 w-4" />
                      Edit HTML
                    </div>
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Email Subject - Always visible */}
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Email Subject
                  </label>
                  <BaseInputText
                    admin={true}
                    {...register("subject", {
                      required: "Email subject is required",
                    })}
                    placeholder="Enter email subject line"
                  />
                </div>

                {/* Stilton Tab Content */}
                {activeTab === "stilton" && (
                  <div className="space-y-6">
                    {/* AI Assistant Section */}
                    {siteKey && (onGenerateWithAI || onEditWithAI) && (
                      <div>
                        <StiltonEmailAssistant
                          siteKey={siteKey}
                          isEditing={isEditing}
                          currentHtmlContent={watchedHtmlContent}
                          currentSubject={watchedSubject}
                          conversationID={conversationID}
                          onGenerate={
                            onGenerateWithAI ||
                            (async () => ({
                              htmlContent: "",
                              subject: "",
                              conversationID: "",
                            }))
                          }
                          onEdit={
                            onEditWithAI ||
                            (async () => ({
                              htmlContent: "",
                              subject: "",
                              conversationID: "",
                            }))
                          }
                          onApplyChanges={handleApplyAIChanges}
                          isLoading={isLoading}
                          onClose={() => setIsAIAssistantOpen(false)}
                        />
                      </div>
                    )}

                    {/* Tips for Stilton AI */}
                    <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                      <h3 className="mb-2 text-sm font-medium text-purple-900">
                        💡 Tips for working with Stilton AI:
                      </h3>
                      <ul className="space-y-1 text-sm text-purple-700">
                        <li>
                          • Be specific about the style, tone, and content you
                          want
                        </li>
                        <li>
                          • Tell Stilton if you want to insert the customer name
                          or date in the email
                        </li>
                        <li>
                          • If you want to add images you can upload them and
                          add them to the email
                        </li>
                        <li>
                          • You can reference images in your request by using
                          "image A", "image B", etc.
                        </li>
                        <li>
                          • Ex: "Put image A at the top, then add some text,
                          then place image B below"
                        </li>
                        <li>
                          • Ask for revisions - Stilton can iterate on the
                          design
                        </li>
                        <li>
                          • If you make manual edits to the HTML or email, you
                          can ask Stilton to clean up the HTML afterwards
                        </li>
                        <li>
                          • Provide the URL of a booking link so Stilton can add
                          it to the email
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* HTML Tab Content */}
                {activeTab === "html" && (
                  <div className="space-y-4">
                    {/* Undo/Redo Controls */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleUndo}
                        disabled={undoStack.length === 0}
                        className="inline-flex items-center rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        title="Undo (Cmd/Ctrl+Z)"
                      >
                        <ArrowUturnLeftIcon className="mr-1.5 h-4 w-4" />
                        Undo
                      </button>
                      <button
                        type="button"
                        onClick={handleRedo}
                        disabled={redoStack.length === 0}
                        className="inline-flex items-center rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        title="Redo (Cmd/Ctrl+Y)"
                      >
                        <ArrowUturnRightIcon className="mr-1.5 h-4 w-4" />
                        Redo
                      </button>
                      <span className="ml-2 text-sm text-gray-400">
                        Use Cmd/Ctrl+Z to undo, Cmd/Ctrl+Y to redo
                      </span>
                    </div>

                    {/* HTML Content Editor */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Email Content (HTML)
                      </label>

                      <textarea
                        {...htmlContentRegister}
                        ref={handleTextareaRef}
                        onChange={(e) => {
                          htmlContentRegister.onChange(e);
                          handleContentChange(e);
                        }}
                        className="block w-full rounded-md border border-gray-300 font-mono text-sm shadow-sm focus:border-primary focus:ring-primary"
                        rows={20}
                        placeholder="Enter your HTML email content here..."
                        onBlur={validateTemplate}
                      />

                      {errors.htmlContent && (
                        <p className="text-sm text-red-600">
                          {errors.htmlContent.message}
                        </p>
                      )}

                      {validationErrors.length > 0 && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-3">
                          <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">
                                Template Validation Errors
                              </h3>
                              <div className="mt-2 text-sm text-red-700">
                                <ul className="list-disc space-y-1 pl-5">
                                  {validationErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quick Tools for HTML mode */}
                    <div className="space-y-4 border-t border-gray-200 pt-4">
                      {/* Variable Insertion */}
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          Insert Customer Variables
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {templateVariables.map((variable) => (
                            <button
                              key={variable.value}
                              type="button"
                              onClick={() => insertVariable(variable.value)}
                              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                            >
                              <TagIcon className="mr-1 h-3 w-3" />
                              {variable.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Image Insertion */}
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          Insert Images
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <ImageSelector
                            onImageInsert={insertImage}
                            size="small"
                            variant="outlined"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Live Preview */}
            <div className="lg:sticky lg:top-4 lg:self-start">
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">
                    Live Preview
                  </h2>
                  {/* Controls */}
                  <div className="flex items-center gap-2">
                    {/* Send Test Email Button */}
                    {templateId && siteKey && (
                      <button
                        type="button"
                        onClick={handleSendTestEmail}
                        className="rounded-md border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
                      >
                        📧 Send Test
                      </button>
                    )}

                    {/* Inline Edit Toggle */}
                    <button
                      type="button"
                      onClick={toggleInlineEditMode}
                      className={`rounded-md border px-3 py-1 text-xs font-medium transition-colors ${
                        isInlineEditMode
                          ? "border-green-300 bg-green-100 text-green-700"
                          : "border-gray-300 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      ✏️{" "}
                      {isInlineEditMode
                        ? "Inline Edit Active"
                        : "Enable Inline Edit"}
                    </button>
                  </div>
                </div>

                {isInlineEditMode && (
                  <div className="mb-3 rounded-md border border-green-200 bg-green-50 p-2">
                    <p className="text-sm text-green-700">
                      <strong>Inline Edit Mode:</strong> Click on any text in
                      the preview below to edit it directly.
                    </p>
                  </div>
                )}

                <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-3">
                  <div className="rounded-md border bg-white p-3 shadow-sm">
                    <div className="mb-3 border-b pb-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Subject Line
                      </p>
                      <p className="mt-1 font-medium text-gray-900">
                        {previewSubject ||
                          "Your email subject will appear here..."}
                      </p>
                    </div>
                    <div className="prose prose-sm max-w-none" ref={previewRef}>
                      {previewHtml ? (
                        <div
                          dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                      ) : (
                        <div className="py-6 text-center italic text-gray-400">
                          Start typing in the email content area to see your
                          preview here...
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  <p>Preview shows sample data for variables:</p>
                  <ul className="mt-1 list-inside list-disc space-y-1">
                    <li>Customer Name: "John Doe"</li>
                    <li>Date: "April 27, 2024"</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Send Test Email Dialog */}
      {isTestEmailDialogOpen && templateId && siteKey && (
        <SendTestEmailDialog
          isOpen={isTestEmailDialogOpen}
          onClose={handleCloseTestEmailDialog}
          templateId={templateId}
          templateName={getValues("name") || "Untitled Template"}
          siteKey={siteKey}
        />
      )}

      {/* Copy Template Dialog */}
      <CopyTemplateDialog
        isOpen={isCopyTemplateDialogOpen}
        onClose={handleCloseCopyTemplateDialog}
        templates={templates}
        onCopyTemplate={handleCopyTemplate}
        loading={templatesLoading}
      />

      {/* Confirmation Dialog for Unsaved Changes */}
      <ConfirmationDialog
        isOpen={isConfirmCancelDialogOpen}
        onClose={handleCancelDialogClose}
        handleConfirmAction={handleConfirmCancel}
        isSubmitting={isCancelling}
        title="Unsaved Changes"
        body={
          <div>
            <p className="mb-4">
              You have unsaved changes in your email template.
            </p>
            <p className="text-sm text-gray-600">
              Are you sure you want to discard these changes and leave this
              page? This action cannot be undone.
            </p>
          </div>
        }
      />
    </div>
  );
}
